import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Building2, CalendarDays, Check, ChevronRight, Clock, Copy, Grid3x3, Image as ImageIcon, ImagePlus, Loader2, MessageSquare, Plus, RefreshCw, Replace, Sparkles, Star, Unplug, X } from 'lucide-react';
import { GoogleBusinessIcon } from './PlatformIcon';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import { MediaItem, uploadMedia } from '../lib/mediaLibrary';
import GoogleBusinessConnectButton from './GoogleBusinessConnectButton';
import { GoogleBusinessInsightsSheet, GoogleBusinessInsightsStrip } from './GoogleBusinessInsights';
import GoogleBusinessLocationsManager from './GoogleBusinessLocationsManager';
import GoogleBusinessPhotosPanel from './GoogleBusinessPhotosPanel';
import GoogleBusinessProfilePanel from './GoogleBusinessProfilePanel';
import MediaLibraryPicker from './MediaLibraryPicker';
import CreateGbpPostModal from './CreateGbpPostModal';
import toast from 'react-hot-toast';

function shortReviewId(raw: string | undefined | null): string {
  if (!raw) return '';
  const s = String(raw);
  return s.includes('/') ? s.split('/').pop()! : s;
}

type Tab = 'reviews' | 'posts' | 'photos';

/** Reviews render in pages — 100+ cards on first paint is wasted work. */
const REVIEWS_PAGE_SIZE = 6;

/**
 * Google user-content URLs accept size directives; ask the CDN for a thumbnail
 * instead of pulling a 12-megapixel original into a 120px tile.
 */
function gbpThumb(url: string, size: number, opts?: { crop?: boolean }): string {
  if (!/^https:\/\/lh\d\.googleusercontent\.com\//i.test(url)) return url;
  return `${url.replace(/=[^/]*$/, '')}=${opts?.crop === false ? `s${size}` : `w${size}-h${size}-c`}`;
}

/** "Tue Sep 15 · 12pm" — compact stamp for the upcoming strip. */
function formatStripWhen(iso: string | null, timeZone?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d
    .toLocaleDateString(undefined, { timeZone, weekday: 'short', month: 'short', day: 'numeric' })
    .replace(',', '');
  const time = d
    .toLocaleTimeString(undefined, { timeZone, hour: 'numeric', minute: '2-digit' })
    .replace(':00', '')
    .replace(/\s/g, '')
    .toLowerCase();
  return `${date} · ${time}`;
}

/** "Sep 24, 2026 · 3:12 PM" — when a review was left, in the business's timezone. */
function formatReviewWhen(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString(undefined, {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, { timeZone, hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
}

type GbpPublishedPost = {
  id: string;
  content: string;
  createdAt: string | null;
  mediaUrl: string | null;
};

type MonthlyMetric = { value: number; deltaPct: number | null };

type GbpMonthlyStats = {
  timeZone: string;
  monthLabel: string;
  interactions: MonthlyMetric | null;
  calls: MonthlyMetric | null;
  chats: MonthlyMetric | null;
  bookings: MonthlyMetric | null;
  directions: MonthlyMetric | null;
  websiteClicks: MonthlyMetric | null;
};

/** 1272 → "1,272", 15400 → "15.4K", 2300000 → "2.3M" */
function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

interface GbpPhoto {
  name?: string;
  mediaFormat?: string;
  sourceUrl?: string;
  googleUrl?: string;
  thumbnailUrl?: string;
  description?: string;
  createTime?: string;
  locationAssociation?: { category?: string };
}

function photoCategory(photo: GbpPhoto): string {
  return String(photo.locationAssociation?.category || 'ADDITIONAL').toUpperCase();
}

/** Prefer displayable CDN URLs; Google often omits or expires one of the three. */
function photoSrcCandidates(photo: GbpPhoto): string[] {
  const raw = [photo.googleUrl, photo.thumbnailUrl, photo.sourceUrl];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of raw) {
    const s = String(u || '').trim();
    if (!s || seen.has(s)) continue;
    if (!/^https?:\/\//i.test(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function photoSrc(photo: GbpPhoto): string {
  return photoSrcCandidates(photo)[0] || '';
}

function isCoverPhoto(photo: GbpPhoto): boolean {
  return photoCategory(photo) === 'COVER';
}

function isProfilePhoto(photo: GbpPhoto): boolean {
  const cat = photoCategory(photo);
  return cat === 'PROFILE' || cat === 'LOGO';
}

/** Google user-content URLs often 403 when the site sends a Referer — strip it + fall back. */
function GbpPhotoImg({
  photo,
  alt,
  className,
  size,
  crop = true,
}: {
  photo: GbpPhoto;
  alt: string;
  className?: string;
  /** Request a CDN thumbnail of roughly this many pixels (Google-hosted images only). */
  size?: number;
  crop?: boolean;
}) {
  const candidates = photoSrcCandidates(photo).map((u) => (size ? gbpThumb(u, size, { crop }) : u));
  const [idx, setIdx] = useState(0);
  const src = candidates[idx] || '';

  if (!src) {
    return (
      <div className={`w-full h-full flex items-center justify-center text-gray-300 ${className || ''}`}>
        <ImageIcon className="h-8 w-8" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      referrerPolicy="no-referrer"
      decoding="async"
      onError={() => {
        if (idx + 1 < candidates.length) setIdx(idx + 1);
      }}
    />
  );
}

interface GbpReview {
  id: string;
  name?: string;
  rating?: number;
  starRating?: string;
  comment?: string | null;
  createTime?: string;
  updateTime?: string;
  reviewer?: {
    displayName?: string;
    profilePhotoUrl?: string | null;
    isAnonymous?: boolean;
  };
  reviewReply?: {
    comment?: string;
    updateTime?: string;
  } | null;
  /** Some Zernio payloads use `reply` instead of `reviewReply`. */
  reply?: {
    comment?: string;
    updateTime?: string;
  } | null;
}

function reviewReplyComment(r: GbpReview): string {
  return String(r.reviewReply?.comment || r.reply?.comment || '').trim();
}

const STAR_MAP: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

function starsFor(r: GbpReview): number {
  if (typeof r.rating === 'number') return r.rating;
  if (r.starRating && STAR_MAP[r.starRating]) return STAR_MAP[r.starRating];
  return 0;
}

interface GoogleBusinessWorkspaceProps {
  /** Notified when connection status changes (e.g. for a tab indicator). */
  onConnectionChange?: (connected: boolean) => void;
  /**
   * `full` — reviews / posts / photos workspace (Media Manager).
   * `connection` — connect / disconnect only (Integrations settings).
   */
  mode?: 'full' | 'connection';
  /** Deep-link into Reviews / Posts / Photos (e.g. from Control Centre quick actions). */
  initialTab?: Tab;
  /** Start Google sign-in immediately in connection mode when nothing is linked. */
  autoStartConnect?: boolean;
}

export default function GoogleBusinessWorkspace({
  onConnectionChange,
  mode = 'full',
  initialTab,
  autoStartConnect = false,
}: GoogleBusinessWorkspaceProps = {}) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const connectionOnly = mode === 'connection';
  const displayTz =
    selectedBusiness?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [connected, setConnected] = useState(false);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [connectedLocations, setConnectedLocations] = useState<
    Array<{
      id: string;
      locationId: string | null;
      locationName: string | null;
      provider: string | null;
      placeId: string | null;
    }>
  >([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [disconnectConnectionId, setDisconnectConnectionId] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  const storageKey = businessId ? `gbp_ws_connection_${businessId}` : null;

  const selectConnection = useCallback(
    (id: string) => {
      setSelectedConnectionId(id);
      if (storageKey) sessionStorage.setItem(storageKey, id);
      const loc = connectedLocations.find((l) => l.id === id);
      if (loc?.locationName) setLocationName(loc.locationName);
    },
    [connectedLocations, storageKey],
  );
  // The Marketing Platforms sheet lists one row per enabled location and
  // announces the pick; switch to it if it's one of ours.
  useEffect(() => {
    const onPick = (e: Event) => {
      const id = String((e as CustomEvent<{ connectionId?: string }>).detail?.connectionId || '');
      if (id && connectedLocations.some((l) => l.id === id)) selectConnection(id);
    };
    window.addEventListener('gbp:select-connection', onPick);
    return () => window.removeEventListener('gbp:select-connection', onPick);
  }, [connectedLocations, selectConnection]);

  // Single-view layout — the old Photos tab now lives in a slide-up sheet.
  const [showPhotos, setShowPhotos] = useState(initialTab === 'photos');

  useEffect(() => {
    if (initialTab === 'photos') setShowPhotos(true);
  }, [initialTab]);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectPreview, setDisconnectPreview] = useState<{
    stagedCount: number;
    autoReplyEnabled: boolean;
    autoPostsEnabled: boolean;
  } | null>(null);
  const [loadingDisconnectPreview, setLoadingDisconnectPreview] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [reviews, setReviews] = useState<GbpReview[]>([]);
  const [avgRating, setAvgRating] = useState<number | null>(null);
  const [totalReviews, setTotalReviews] = useState<number | null>(null);
  /** When this Google connection was made — reviews older than this may have
   *  replies on Google that haven't synced yet. */
  const [connectedAt, setConnectedAt] = useState<string | null>(null);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'replied' | 'unreplied'>('all');
  const [reviewsVisible, setReviewsVisible] = useState(REVIEWS_PAGE_SIZE);
  useEffect(() => {
    setReviewsVisible(REVIEWS_PAGE_SIZE);
  }, [reviewFilter, selectedConnectionId]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [aiReplyIds, setAiReplyIds] = useState<Set<string>>(new Set());
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [replyingId, setReplyingId] = useState<string | null>(null);

  const [composerOpen, setComposerOpen] = useState(false);
  const [pendingDrafts, setPendingDrafts] = useState<
    Array<{
      id: string;
      content: string;
      cta_type: string | null;
      cta_url: string | null;
      tokens_used: number | null;
      created_at: string;
      scheduled_for?: string | null;
      source?: 'ai' | 'manual' | string | null;
      has_image?: boolean;
      media_id?: string | null;
      media_library?: { url: string } | { url: string }[] | null;
    }>
  >([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [fillingQueue, setFillingQueue] = useState(false);
  const [draftActionId, setDraftActionId] = useState<string | null>(null);
  const [autoPostsEnabled, setAutoPostsEnabled] = useState(false);
  const [openDraft, setOpenDraft] = useState<(typeof pendingDrafts)[number] | null>(null);

  const [publishedPosts, setPublishedPosts] = useState<GbpPublishedPost[]>([]);
  const [loadingPublished, setLoadingPublished] = useState(false);
  const [openPost, setOpenPost] = useState<GbpPublishedPost | null>(null);
  const [openReview, setOpenReview] = useState<GbpReview | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<GbpMonthlyStats | null>(null);

  const [photos, setPhotos] = useState<GbpPhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadMode, setPhotoUploadMode] = useState<'additional' | 'cover'>('additional');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showCoverSwapMenu, setShowCoverSwapMenu] = useState(false);
  const [showCoverFromGbp, setShowCoverFromGbp] = useState(false);
  const photoFileInputRef = React.useRef<HTMLInputElement>(null);

  /** Direct Google connection — unlocks insights, photo management and profile editing. */
  const isNativeGoogle =
    connectedLocations.find((l) => l.id === selectedConnectionId)?.provider === 'google';
  const [showInsights, setShowInsights] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  /** Google's "write a review" deep link for the selected location. */
  const selectedPlaceId =
    connectedLocations.find((l) => l.id === selectedConnectionId)?.placeId || null;
  const reviewLink = selectedPlaceId
    ? `https://search.google.com/local/writereview?placeid=${encodeURIComponent(selectedPlaceId)}`
    : null;
  const [reviewLinkCopied, setReviewLinkCopied] = useState(false);
  const copyReviewLink = async () => {
    if (!reviewLink) return;
    try {
      await navigator.clipboard.writeText(reviewLink);
      setReviewLinkCopied(true);
      toast.success('Review link copied');
      window.setTimeout(() => setReviewLinkCopied(false), 1800);
    } catch {
      window.prompt('Copy your Google review link', reviewLink);
    }
  };
  /** How the Photos sheet should open: straight into the cover menu, or onto one photo. */
  const [photosEntry, setPhotosEntry] = useState<{
    roleMenu: 'COVER' | 'PROFILE' | null;
    detailName: string | null;
  }>({ roleMenu: null, detailName: null });
  const openPhotos = (entry?: Partial<typeof photosEntry>) => {
    setPhotosEntry({ roleMenu: null, detailName: null, ...entry });
    setShowPhotos(true);
  };

  const refreshConnection = useCallback(async () => {
    if (!businessId) {
      setConnected(false);
      setLocationName(null);
      setConnectedLocations([]);
      setSelectedConnectionId(null);
      onConnectionChange?.(false);
      setChecking(false);
      return;
    }
    setChecking(true);
    try {
      const { data } = await supabase
        .from('google_business_connections')
        .select('id, location_id, location_name, provider, place_id')
        .eq('business_id', businessId)
        .in('provider', ['ayrshare', 'google'])
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      const rows = data || [];
      const isConnected = rows.length > 0;
      setConnected(isConnected);
      const mapped = rows
        .map((r) => ({
          id: r.id,
          locationId: r.location_id,
          locationName: r.location_name,
          provider: r.provider as string | null,
          placeId: (r.place_id as string | null) || null,
        }))
        // Direct Google connections first — they unlock the full feature set.
        .sort((a, b) => (a.provider === b.provider ? 0 : a.provider === 'google' ? -1 : 1));
      setConnectedLocations(mapped);

      const stored = storageKey ? sessionStorage.getItem(storageKey) : null;
      const preferred =
        (stored && mapped.some((m) => m.id === stored) ? stored : null) ||
        mapped[0]?.id ||
        null;
      setSelectedConnectionId(preferred);
      if (preferred && storageKey) sessionStorage.setItem(storageKey, preferred);
      setLocationName(mapped.find((m) => m.id === preferred)?.locationName || null);

      onConnectionChange?.(isConnected);
    } finally {
      setChecking(false);
    }
  }, [businessId, onConnectionChange, storageKey]);

  const loadAiReplyIds = useCallback(async () => {
    if (!businessId) {
      setAiReplyIds(new Set());
      return;
    }

    const ids = new Set<string>();

    try {
      const [{ data: logRows }, { data: historyRows }] = await Promise.all([
        supabase
          .from('google_business_auto_reply_log')
          .select('review_id')
          .eq('business_id', businessId)
          .eq('status', 'posted'),
        supabase
          .from('google_business_history')
          .select('review_id')
          .eq('business_id', businessId)
          .eq('event_type', 'auto_reply')
          .not('review_id', 'is', null),
      ]);

      for (const row of logRows || []) {
        const id = shortReviewId(row.review_id);
        if (id) ids.add(id);
      }
      for (const row of historyRows || []) {
        const id = shortReviewId(row.review_id);
        if (id) ids.add(id);
      }
    } catch (err) {
      console.warn('Could not load AI reply tags:', err);
    }

    setAiReplyIds(ids);
  }, [businessId]);

  const loadReviews = useCallback(async (opts?: { force?: boolean }) => {
    if (!businessId || !connected || !selectedConnectionId) return;
    setLoadingReviews(true);
    // Warn-level so it shows under Default/Warnings even when Info is filtered out.
    console.warn('[GBP reviews] loading…', { businessId, connectionId: selectedConnectionId });
    try {
      const [{ data, error }] = await Promise.all([
        supabase.functions.invoke('ayrshare-gbp-reviews', {
          body: {
            businessId,
            connectionId: selectedConnectionId,
            pageSize: 50,
            force: opts?.force === true,
          },
        }),
        loadAiReplyIds(),
      ]);
      if (error || data?.error) {
        const code = typeof data?.code === 'string' ? data.code : '';
        const msg =
          code === 'needs_reconnect' || code === 'stale_zernio_account'
            ? 'Google connection expired — reconnect Google Business Profile.'
            : (await invokeErrorMessage(error, data)) || 'Failed to load reviews';
        console.error('[GBP reviews] failed:', error, data);
        toast.error(msg);
        if (code === 'needs_reconnect' || code === 'stale_zernio_account') {
          await refreshConnection();
        }
        return;
      }
      const list = Array.isArray(data?.reviews)
        ? data.reviews
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const withReply = list.filter((r: GbpReview) => !!reviewReplyComment(r)).length;
      const sample = list[0] as GbpReview | undefined;
      console.warn('[GBP reviews]', {
        locationName: data?.locationName,
        matchedBy: data?.matchedBy,
        source: data?.source,
        total: list.length,
        withReply,
        replyStats: data?.replyStats,
        sampleKeys: data?.sampleKeys || (sample ? Object.keys(sample) : []),
        sampleReplyComment:
          data?.sampleReplyComment ?? (reviewReplyComment(sample || {}) || null),
        sampleReviewReply: (sample as { reviewReply?: unknown } | undefined)?.reviewReply ?? null,
        debug: data?.debug ?? null,
      });
      setReviews(list);
      setAvgRating(typeof data?.averageRating === 'number' ? data.averageRating : null);
      setTotalReviews(
        typeof data?.totalReviewCount === 'number' ? data.totalReviewCount : list.length,
      );
      setConnectedAt(typeof data?.connectedAt === 'string' ? data.connectedAt : null);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load reviews');
    } finally {
      setLoadingReviews(false);
    }
  }, [businessId, connected, selectedConnectionId, loadAiReplyIds, refreshConnection]);

  useEffect(() => {
    void refreshConnection();
  }, [refreshConnection]);

  // Clear stale review/media UI immediately when switching Nexus businesses
  // or Google locations — never leave another location's reviews on screen.
  useEffect(() => {
    setReviews([]);
    setAvgRating(null);
    setTotalReviews(null);
    setReplyDrafts({});
    setPendingDrafts([]);
    setPhotos([]);
    setPublishedPosts([]);
    setOpenDraft(null);
    setOpenPost(null);
    setOpenReview(null);
    setMonthlyStats(null);
  }, [businessId, selectedConnectionId]);

  const loadAutoPostSettings = useCallback(async () => {
    if (!businessId || !selectedConnectionId) {
      setAutoPostsEnabled(false);
      return;
    }

    const { data } = await supabase
      .from('google_business_auto_post_settings')
      .select('enabled')
      .eq('connection_id', selectedConnectionId)
      .maybeSingle();
    setAutoPostsEnabled(!!data?.enabled);
  }, [businessId, selectedConnectionId]);

  const loadPendingDrafts = useCallback(async () => {
    if (!businessId || !connected || !selectedConnectionId) {
      setPendingDrafts([]);
      return;
    }
    setLoadingDrafts(true);
    try {
      // media_library has two FKs to/from drafts (media_id + gbp_post_draft_id),
      // so PostgREST needs an explicit FK hint or the embed fails silently via
      // our fallback chain — leaving has_image true but no thumbnail URL.
      const attempts = [
        'id, content, cta_type, cta_url, tokens_used, created_at, scheduled_for, source, has_image, media_id, media_library!google_business_post_drafts_media_id_fkey(url)',
        'id, content, cta_type, cta_url, tokens_used, created_at, scheduled_for, source, has_image, media_id, media_library!media_id(url)',
        'id, content, cta_type, cta_url, tokens_used, created_at, scheduled_for, source, has_image, media_id',
        'id, content, cta_type, cta_url, tokens_used, created_at, scheduled_for, source',
        'id, content, cta_type, cta_url, tokens_used, created_at, scheduled_for',
        'id, content, cta_type, cta_url, tokens_used, created_at',
      ];

      let rows: typeof pendingDrafts = [];
      let lastError: unknown = null;
      for (const select of attempts) {
        const { data, error } = await supabase
          .from('google_business_post_drafts')
          .select(select)
          .eq('business_id', businessId)
          .eq('connection_id', selectedConnectionId)
          .eq('status', 'pending')
          .order('created_at', { ascending: true })
          .limit(20);
        if (!error) {
          rows = (data as typeof pendingDrafts) || [];
          lastError = null;
          break;
        }
        lastError = error;
      }
      if (lastError) throw lastError;

      // If the join still didn't attach URLs (or we fell back), hydrate from
      // media_library by media_id so staged image posts always show a preview.
      const missingUrlIds = rows
        .filter((r) => {
          if (!r.media_id && !r.has_image) return false;
          const rel = r.media_library;
          const url = Array.isArray(rel) ? rel[0]?.url : rel?.url;
          return !!r.media_id && !url;
        })
        .map((r) => r.media_id as string);

      if (missingUrlIds.length > 0) {
        const { data: mediaRows } = await supabase
          .from('media_library')
          .select('id, url')
          .in('id', [...new Set(missingUrlIds)]);
        const urlById = new Map(
          (mediaRows || []).map((m) => [m.id as string, m.url as string]),
        );
        rows = rows.map((r) => {
          if (!r.media_id) return r;
          const rel = r.media_library;
          const existing = Array.isArray(rel) ? rel[0]?.url : rel?.url;
          if (existing) return r;
          const url = urlById.get(r.media_id);
          return url ? { ...r, media_library: { url } } : r;
        });
      }

      rows.sort((a, b) => {
        const ta = a.scheduled_for ? new Date(a.scheduled_for).getTime() : Number.MAX_SAFE_INTEGER;
        const tb = b.scheduled_for ? new Date(b.scheduled_for).getTime() : Number.MAX_SAFE_INTEGER;
        if (ta !== tb) return ta - tb;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
      setPendingDrafts(rows);
      return rows;
    } catch (err) {
      console.error('Failed to load pending post drafts:', err);
      setPendingDrafts([]);
      return [];
    } finally {
      setLoadingDrafts(false);
    }
  }, [businessId, connected, selectedConnectionId]);

  const loadPublishedPosts = useCallback(async () => {
    if (!businessId || !connected || !selectedConnectionId) {
      setPublishedPosts([]);
      return;
    }
    setLoadingPublished(true);
    try {
      if (isNativeGoogle) {
        // Direct Google: every post on the listing, wherever it was created.
        const { data, error } = await supabase.functions.invoke('google-gbp-posts', {
          body: { action: 'list', businessId, connectionId: selectedConnectionId, limit: 30 },
        });
        if (error || data?.error) {
          throw new Error((await invokeErrorMessage(error, data)) || 'Failed to load posts');
        }
        const posts = (Array.isArray(data?.posts) ? data.posts : []) as Array<{
          name: string;
          summary: string;
          createTime: string | null;
          mediaUrls: string[];
          topicType: string;
          event?: { title: string | null } | null;
        }>;
        setPublishedPosts(
          posts.map((p) => ({
            id: p.name,
            content: p.summary || p.event?.title || '',
            createdAt: p.createTime,
            mediaUrl: p.mediaUrls[0] || null,
          })),
        );
        return;
      }

      // Aggregator connections: only posts published through Nexus are known.
      // AI draft logs share event_type 'post' but carry metadata.status
      // 'pending', and older rows may have a null connection_id — filter both.
      const { data, error } = await supabase
        .from('google_business_history')
        .select('id, content, created_at, connection_id, metadata')
        .eq('business_id', businessId)
        .eq('event_type', 'post')
        .order('created_at', { ascending: false })
        .limit(60);
      if (error) throw error;
      const rows = (data || [])
        .filter((r) => {
          const meta = (r.metadata || {}) as { status?: string };
          if (meta.status === 'pending') return false;
          return !r.connection_id || r.connection_id === selectedConnectionId;
        })
        .slice(0, 24)
        .map((r) => {
          const meta = (r.metadata || {}) as { media_url?: string | null };
          return {
            id: r.id as string,
            content: String(r.content || ''),
            createdAt: (r.created_at as string) || null,
            mediaUrl: meta.media_url || null,
          };
        });
      setPublishedPosts(rows);
    } catch (err) {
      console.error('Failed to load published GBP posts:', err);
      setPublishedPosts([]);
    } finally {
      setLoadingPublished(false);
    }
  }, [businessId, connected, selectedConnectionId, isNativeGoogle]);

  const loadMonthlyStats = useCallback(async () => {
    if (!businessId || !connected || !selectedConnectionId) {
      setMonthlyStats(null);
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('ayrshare-gbp-analytics', {
        body: { businessId, connectionId: selectedConnectionId },
      });
      if (error || data?.error) {
        console.warn('[GBP analytics] failed:', error, data);
        return;
      }
      setMonthlyStats((data?.monthlyStats as GbpMonthlyStats) || null);
    } catch (err) {
      console.error('[GBP analytics] failed:', err);
    }
  }, [businessId, connected, selectedConnectionId]);

  useEffect(() => {
    if (connectionOnly || isNativeGoogle) return;
    if (connected && selectedConnectionId) void loadMonthlyStats();
  }, [connectionOnly, isNativeGoogle, connected, selectedConnectionId, loadMonthlyStats]);

  const ensureDraftQueue = useCallback(async () => {
    if (!businessId || !connected || !autoPostsEnabled || !selectedConnectionId) return;
    setFillingQueue(true);
    try {
      const { data, error } = await supabase.functions.invoke('gbp-auto-post-action', {
        body: { action: 'fill_queue', businessId, connectionId: selectedConnectionId },
      });
      if (error || data?.error) {
        const msg = data?.error || (await invokeErrorMessage(error, data)) || 'Failed to stage drafts';
        toast.error(msg);
        return;
      }
      if ((data?.created ?? 0) > 0) {
        toast.success(`Staged ${data.created} AI post${data.created === 1 ? '' : 's'}`);
      } else if (data?.nextRunAt) {
        toast.success('Publish dates updated');
      }
      await loadPendingDrafts();
    } catch (err) {
      console.error(err);
      toast.error('Failed to stage AI posts');
    } finally {
      setFillingQueue(false);
    }
  }, [businessId, connected, autoPostsEnabled, selectedConnectionId, loadPendingDrafts]);

  useEffect(() => {
    if (connectionOnly) return;
    if (connected && selectedConnectionId) void loadReviews();
  }, [connectionOnly, connected, selectedConnectionId, loadReviews]);

  useEffect(() => {
    if (connectionOnly) return;
    if (connected && selectedConnectionId) void loadPublishedPosts();
  }, [connectionOnly, connected, selectedConnectionId, loadPublishedPosts]);

  useEffect(() => {
    if (connectionOnly || !connected || !selectedConnectionId) return;
    void loadAutoPostSettings();
    void loadPendingDrafts();
  }, [connectionOnly, connected, selectedConnectionId, loadAutoPostSettings, loadPendingDrafts]);

  // Top up to 5 AND assign dates if any staged post is missing scheduled_for
  useEffect(() => {
    if (connectionOnly || !connected || !selectedConnectionId || !autoPostsEnabled) return;
    let cancelled = false;
    (async () => {
      const rows = await loadPendingDrafts();
      if (cancelled) return;
      const aiRows = (rows || []).filter((r) => (r.source || 'ai') === 'ai');
      const needsFill = aiRows.length < 5;
      const needsDates = aiRows.some((r) => !r.scheduled_for);
      if (needsFill || needsDates) {
        await ensureDraftQueue();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connectionOnly, connected, selectedConnectionId, autoPostsEnabled, loadPendingDrafts, ensureDraftQueue]);

  const handleDraftAction = async (draftId: string, action: 'approve' | 'reject') => {
    if (!businessId || !selectedConnectionId) return;
    setDraftActionId(draftId);
    try {
      const { data, error } = await supabase.functions.invoke('gbp-auto-post-action', {
        body: { action, businessId, draftId, connectionId: selectedConnectionId },
      });
      if (error || data?.error) {
        toast.error(data?.error || `Failed to ${action} draft`);
        return;
      }
      toast.success(action === 'approve' ? 'Post published to Google' : 'Draft rejected');
      setOpenDraft(null);
      await Promise.all([
        loadPendingDrafts(),
        action === 'approve' ? loadPublishedPosts() : Promise.resolve(),
      ]);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${action} draft`);
    } finally {
      setDraftActionId(null);
    }
  };

  const loadPhotos = useCallback(async (opts?: { silent?: boolean }) => {
    if (!businessId || !connected || !selectedConnectionId) return;
    setLoadingPhotos(true);
    try {
      // Native Google reads the real gallery; aggregator connections only
      // know about photos published through Nexus.
      const { data, error } = await supabase.functions.invoke(
        isNativeGoogle ? 'google-gbp-media' : 'ayrshare-gbp-media',
        {
          body: {
            action: 'list',
            businessId,
            connectionId: selectedConnectionId,
            pageSize: 100,
            // Workspace only needs the owner gallery (cover, logo, first tiles).
            scope: 'owner',
            maxPages: 1,
          },
        },
      );
      if (error || data?.error) {
        if (!opts?.silent) {
          const msg = (await invokeErrorMessage(error, data)) || 'Failed to load photos';
          toast.error(msg);
        }
        return;
      }
      setPhotos(Array.isArray(data?.mediaItems) ? data.mediaItems : []);
    } catch (err) {
      console.error(err);
      if (!opts?.silent) toast.error('Failed to load photos');
    } finally {
      setLoadingPhotos(false);
    }
  }, [businessId, connected, selectedConnectionId, isNativeGoogle]);

  useEffect(() => {
    if (connectionOnly || isNativeGoogle) return;
    if (connected && selectedConnectionId && showPhotos) void loadPhotos();
  }, [connectionOnly, isNativeGoogle, connected, selectedConnectionId, showPhotos, loadPhotos]);

  // Warm profile/cover photos for the header avatar without forcing the Photos tab open.
  useEffect(() => {
    if (connectionOnly || !connected || !selectedConnectionId) return;
    if (photos.length > 0) return;
    void loadPhotos({ silent: true });
  }, [connectionOnly, connected, selectedConnectionId, photos.length, loadPhotos]);

  const uploadPhotoToGoogle = async (
    sourceUrl: string,
    category: 'ADDITIONAL' | 'COVER' = 'ADDITIONAL',
  ) => {
    if (!businessId || !selectedConnectionId) return;
    setUploadingPhoto(true);
    setShowCoverSwapMenu(false);
    setShowCoverFromGbp(false);
    try {
      const { data, error } = await supabase.functions.invoke('ayrshare-gbp-media', {
        body: {
          action: 'upload',
          businessId,
          connectionId: selectedConnectionId,
          sourceUrl,
          category,
        },
      });
      if (error || data?.error) {
        const msg = (await invokeErrorMessage(error, data)) || 'Failed to add photo';
        toast.error(msg);
        return;
      }
      toast.success(
        category === 'COVER'
          ? 'Cover photo updated'
          : 'Photo added to Google Business Profile',
      );
      await loadPhotos();
    } catch (err) {
      console.error(err);
      toast.error('Failed to add photo');
    } finally {
      setUploadingPhoto(false);
      setPhotoUploadMode('additional');
    }
  };

  const handleLibraryPick = async (selected: MediaItem[]) => {
    setShowMediaPicker(false);
    if (!selected[0]?.url) return;
    await uploadPhotoToGoogle(
      selected[0].url,
      photoUploadMode === 'cover' ? 'COVER' : 'ADDITIONAL',
    );
  };

  const handlePhotoFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!businessId) return;
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    const category = photoUploadMode === 'cover' ? 'COVER' : 'ADDITIONAL';
    setUploadingPhoto(true);
    try {
      const [created] = await uploadMedia(businessId, [file]);
      if (!created?.url) throw new Error('Upload did not return a URL');
      await uploadPhotoToGoogle(created.url, category);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Upload failed');
      setUploadingPhoto(false);
      setPhotoUploadMode('additional');
    }
  };

  const startCoverSwapUpload = () => {
    setPhotoUploadMode('cover');
    setShowCoverSwapMenu(false);
    photoFileInputRef.current?.click();
  };

  const startCoverSwapFromMedia = () => {
    setPhotoUploadMode('cover');
    setShowCoverSwapMenu(false);
    setShowMediaPicker(true);
  };

  const startCoverSwapFromGbp = () => {
    setShowCoverSwapMenu(false);
    setShowCoverFromGbp(true);
  };

  /** True when the review predates the Google connection — its reply status
   *  may not have synced from Google yet, so replying could overwrite an
   *  existing owner reply. */
  const isPreConnectionReview = useCallback(
    (review: GbpReview) => {
      if (!connectedAt || !review.createTime) return false;
      const created = Date.parse(review.createTime);
      const connected = Date.parse(connectedAt);
      return created > 0 && connected > 0 && created < connected;
    },
    [connectedAt],
  );

  const handleReply = async (review: GbpReview) => {
    if (!businessId || !selectedConnectionId) return;
    const comment = (replyDrafts[review.id] || '').trim();
    if (!comment) {
      toast.error('Write a reply first');
      return;
    }
    if (isPreConnectionReview(review) && !reviewReplyComment(review)) {
      const confirmed = window.confirm(
        'This review is from before you connected Google Business, and Google may still be syncing its reply data.\n\n' +
          'If you already replied to this review on Google, posting now will REPLACE that reply.\n\n' +
          'Check the review on Google first if unsure. Post this reply?',
      );
      if (!confirmed) return;
    }
    setReplyingId(review.id);
    try {
      const { data, error } = await supabase.functions.invoke('ayrshare-gbp-reply', {
        body: {
          businessId,
          connectionId: selectedConnectionId,
          reviewId: review.id,
          comment,
          starRating: starsFor(review),
          reviewerName: review.reviewer?.displayName || null,
        },
      });
      if (error || data?.error) {
        toast.error(data?.error || 'Failed to post reply');
        return;
      }
      toast.success('Reply posted');
      setReplyDrafts((prev) => ({ ...prev, [review.id]: '' }));
      setOpenReview(null);
      await loadReviews();
    } catch (e) {
      console.error(e);
      toast.error('Failed to post reply');
    } finally {
      setReplyingId(null);
    }
  };

  const openDisconnectModal = async (connectionId?: string | null) => {
    if (!businessId) return;
    setDisconnectConnectionId(connectionId || null);
    setShowDisconnectModal(true);
    setLoadingDisconnectPreview(true);
    setDisconnectPreview(null);
    try {
      const { data, error } = await supabase.functions.invoke('ayrshare-gbp-disconnect', {
        body: {
          action: 'preview',
          businessId,
          ...(connectionId ? { connectionId } : {}),
        },
      });
      if (error || data?.error) {
        toast.error(data?.error || 'Failed to load disconnect details');
        setShowDisconnectModal(false);
        return;
      }
      setDisconnectPreview({
        stagedCount: data.stagedCount || 0,
        autoReplyEnabled: !!data.autoReplyEnabled,
        autoPostsEnabled: !!data.autoPostsEnabled,
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load disconnect details');
      setShowDisconnectModal(false);
    } finally {
      setLoadingDisconnectPreview(false);
    }
  };

  const confirmDisconnect = async () => {
    if (!businessId) return;
    setDisconnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('ayrshare-gbp-disconnect', {
        body: {
          action: 'disconnect',
          businessId,
          ...(disconnectConnectionId ? { connectionId: disconnectConnectionId } : {}),
        },
      });
      if (error || data?.error) {
        toast.error(data?.error || 'Failed to disconnect');
        return;
      }
      toast.success(
        disconnectConnectionId
          ? 'Location disconnected — staged posts and automations for it were cleared'
          : 'Google Business disconnected — staged posts and automations cleared',
      );
      setShowDisconnectModal(false);
      setDisconnectPreview(null);
      setDisconnectConnectionId(null);
      setPendingDrafts([]);
      await refreshConnection();
    } catch (err) {
      console.error(err);
      toast.error('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const disconnectModal = showDisconnectModal ? (
    <div className="fixed inset-0 z-[100110] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => !disconnecting && setShowDisconnectModal(false)}
      />
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            {disconnectConnectionId
              ? 'Disconnect this location?'
              : 'Disconnect Google Business?'}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            This cannot be undone. You can reconnect later, but staged work and automations for
            {disconnectConnectionId ? ' this location' : ' Google Business'} will be cleared.
          </p>
        </div>
        <div className="px-5 py-4 space-y-3">
          {loadingDisconnectPreview ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking what will be removed…
            </div>
          ) : (
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                <span className="font-semibold text-amber-900 shrink-0">
                  {disconnectPreview?.stagedCount ?? 0}
                </span>
                <span className="text-amber-900/90">
                  staged post{(disconnectPreview?.stagedCount ?? 0) === 1 ? '' : 's'} will be
                  permanently removed
                </span>
              </li>
              {disconnectPreview?.autoReplyEnabled ? (
                <li className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-red-800">
                  Auto-replies are on — they will be turned off
                </li>
              ) : null}
              {disconnectPreview?.autoPostsEnabled ? (
                <li className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-red-800">
                  Auto posts are on — they will be turned off and the AI draft queue cleared
                </li>
              ) : null}
              {!disconnectPreview?.autoReplyEnabled &&
              !disconnectPreview?.autoPostsEnabled &&
              (disconnectPreview?.stagedCount ?? 0) === 0 ? (
                <li className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-gray-600">
                  No staged posts or active automations — connection will simply be removed
                </li>
              ) : null}
            </ul>
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            disabled={disconnecting}
            onClick={() => setShowDisconnectModal(false)}
            className="px-4 py-2.5 text-sm font-semibold text-gray-700 rounded-xl border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            Keep connected
          </button>
          <button
            type="button"
            disabled={disconnecting || loadingDisconnectPreview}
            onClick={() => void confirmDisconnect()}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50"
          >
            {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />}
            Disconnect & clear
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (checking) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (connectionOnly) {
    const legacyLocations = connectedLocations.filter((l) => l.provider !== 'google');
    return (
      <div className="space-y-4">
        {legacyLocations.length > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
            <div>
              <p className="text-[13px] font-semibold text-amber-900">
                Connected through the old link — sign in with Google to upgrade
              </p>
              <p className="text-[12px] text-amber-800 mt-0.5">
                Your posts, replies and automation carry over automatically when you connect the
                same location below.
              </p>
            </div>
            {legacyLocations.map((loc) => (
              <div
                key={loc.id}
                className="bg-white/70 border border-amber-200 rounded-xl px-3 py-2.5 flex items-center gap-3"
              >
                <GoogleBusinessIcon className="h-8 w-8 shrink-0 grayscale opacity-70" />
                <p className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">
                  {loc.locationName || 'Google Business location'}
                </p>
                <button
                  type="button"
                  onClick={() => void openDisconnectModal(loc.id)}
                  className="text-xs font-semibold text-red-600 hover:text-red-700 shrink-0"
                >
                  Disconnect
                </button>
              </div>
            ))}
          </div>
        ) : null}
        {businessId ? (
          <GoogleBusinessLocationsManager
            businessId={businessId}
            autoStart={autoStartConnect}
            onChanged={() => void refreshConnection()}
          />
        ) : null}
        {disconnectModal}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!connected && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            Connect your Google Business Profile to see reviews, reply, and publish posts.
          </p>
          <GoogleBusinessConnectButton
            size="medium"
            hideStatusIndicator
            onConnectionChange={() => {
              void refreshConnection().then(() => {
                void loadReviews();
              });
            }}
          />
        </div>
      )}

      {connected && (
        <>
          {/* Legacy aggregator link — nudge to the direct Google connection */}
          {!isNativeGoogle && selectedConnectionId && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 space-y-2">
              <p className="text-[13px] font-semibold text-amber-900">
                Reconnect with Google to unlock the full profile
              </p>
              <p className="text-[12px] text-amber-800">
                Performance insights, your real photo gallery, cover &amp; logo control and
                profile editing all need a direct Google sign-in. Posts, replies and automation
                settings carry over automatically.
              </p>
              <GoogleBusinessConnectButton
                size="small"
                hideStatusIndicator
                hideHelperText
                onConnectionChange={() => {
                  void refreshConnection().then(() => {
                    void loadReviews({ force: true });
                  });
                }}
              />
            </div>
          )}

          {/* Native Google — live Performance API summary */}
          {isNativeGoogle && businessId && selectedConnectionId && (
            <GoogleBusinessInsightsStrip
              businessId={businessId}
              connectionId={selectedConnectionId}
              onOpen={() => setShowInsights(true)}
            />
          )}

          {/* Month-to-date profile metrics (business-timezone month, computed server-side) */}
          {!isNativeGoogle && monthlyStats && (
            <section className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {monthlyStats.monthLabel} so far
              </p>
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                {(
                  [
                    ['Interactions', monthlyStats.interactions],
                    ['Calls', monthlyStats.calls],
                    ['Chats', monthlyStats.chats],
                    ['Bookings', monthlyStats.bookings],
                    ['Directions', monthlyStats.directions],
                    ['Website clicks', monthlyStats.websiteClicks],
                  ] as const
                ).map(([label, metric]) => (
                  <div
                    key={label}
                    className="rounded-xl border border-gray-200 bg-white px-2.5 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.04)] min-w-0"
                  >
                    <div className="flex items-baseline gap-1 min-w-0">
                      <span className="text-[15px] font-bold text-gray-900 leading-tight truncate">
                        {metric ? fmtCount(metric.value) : '—'}
                      </span>
                      {metric?.deltaPct != null && Math.round(Math.abs(metric.deltaPct)) > 0 ? (
                        <span
                          className={`text-[10px] font-semibold shrink-0 ${
                            metric.deltaPct > 0 ? 'text-green-600' : 'text-red-500'
                          }`}
                        >
                          {metric.deltaPct > 0 ? '▲' : '▼'}
                          {Math.round(Math.abs(metric.deltaPct))}%
                        </span>
                      ) : null}
                    </div>
                    <p className="text-[10px] text-gray-500 truncate mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
              {[
                monthlyStats.interactions,
                monthlyStats.calls,
                monthlyStats.chats,
                monthlyStats.bookings,
                monthlyStats.directions,
                monthlyStats.websiteClicks,
              ].every((m) => m == null) && (
                <p className="text-[10px] text-gray-400">
                  Tracking started today — this month's numbers appear from tomorrow
                </p>
              )}
            </section>
          )}

          {(() => {
            const displayName = locationName || 'Google Business';
            const profilePhoto = photos.find(isProfilePhoto) || null;
            const avatarSrc = profilePhoto ? gbpThumb(photoSrc(profilePhoto), 96) : null;
            const refreshing =
              loadingReviews || loadingDrafts || fillingQueue || loadingPublished;

            const handleHeaderRefresh = () => {
              void loadReviews({ force: true });
              void loadPendingDrafts();
              void loadPublishedPosts();
              void loadMonthlyStats();
              if (showPhotos) void loadPhotos();
            };

            return (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  {/* Native Google: the avatar is the logo — tap to change it */}
                  <button
                    type="button"
                    onClick={isNativeGoogle ? () => openPhotos({ roleMenu: 'PROFILE' }) : undefined}
                    disabled={!isNativeGoogle}
                    aria-label={isNativeGoogle ? 'Change logo' : undefined}
                    title={isNativeGoogle ? 'Change logo' : undefined}
                    className={`relative shrink-0 rounded-full ${
                      isNativeGoogle ? 'group cursor-pointer touch-manipulation' : 'cursor-default'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover bg-[#e4e6eb]"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`h-10 w-10 rounded-full bg-white border border-gray-200 items-center justify-center overflow-hidden ${
                        avatarSrc ? 'hidden' : 'flex'
                      }`}
                      aria-hidden
                    >
                      <GoogleBusinessIcon className="h-10 w-10" square />
                    </div>
                    {isNativeGoogle ? (
                      <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-600">
                        <Replace className="h-2.5 w-2.5" />
                      </span>
                    ) : null}
                  </button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <h2
                        className="text-[17px] font-bold text-gray-900 truncate leading-tight"
                        style={{ letterSpacing: '-0.02em' }}
                      >
                        {displayName}
                      </h2>
                      <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" aria-hidden />
                    </div>
                    <p className="text-[12px] text-gray-500 truncate">Google Business Profile</p>
                    {(avgRating != null || totalReviews != null) && (
                      <p className="text-[12px] text-gray-500 truncate mt-0.5">
                        {avgRating != null && (
                          <>
                            <span className="font-semibold text-gray-900">
                              {avgRating.toFixed(1)}
                            </span>{' '}
                            <span className="text-amber-400">★</span>
                          </>
                        )}
                        {avgRating != null && totalReviews != null && (
                          <span className="mx-1 text-gray-300">·</span>
                        )}
                        {totalReviews != null && (
                          <>
                            <span className="font-semibold text-gray-900">{totalReviews}</span>{' '}
                            reviews
                          </>
                        )}
                      </p>
                    )}
                    {reviewLink && (
                      <div className="mt-1 flex items-center gap-1 min-w-0">
                        <a
                          href={reviewLink}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 min-w-0 text-[11px] text-[#1a73e8] hover:underline"
                          title="Open your Google review link"
                        >
                          <Star className="h-3 w-3 shrink-0 fill-amber-400 text-amber-400" />
                          <span className="truncate">{reviewLink.replace(/^https?:\/\//, '')}</span>
                        </a>
                        <button
                          type="button"
                          onClick={() => void copyReviewLink()}
                          aria-label="Copy review link"
                          title="Copy review link"
                          className="h-6 w-6 shrink-0 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {reviewLinkCopied ? (
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {isNativeGoogle ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowInsights(true)}
                        aria-label="Insights"
                        className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <BarChart3 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowProfile(true)}
                        aria-label="Business profile details"
                        className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Building2 className="h-4 w-4" />
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setShowPhotos(true)}
                    aria-label="Photos"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <ImageIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleHeaderRefresh}
                    disabled={refreshing}
                    aria-label="Refresh"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {refreshing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setComposerOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[10px] bg-[#1a73e8] text-white text-[13px] font-semibold hover:bg-[#1765cc]"
                    style={{ minHeight: 36 }}
                  >
                    <Plus className="h-4 w-4" />
                    New post
                  </button>
                </div>
              </div>
            );
          })()}

          {connectedLocations.length > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
              <label
                htmlFor="gbp-location-switcher"
                className="text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0"
              >
                Location
              </label>
              <select
                id="gbp-location-switcher"
                value={selectedConnectionId || ''}
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  selectConnection(id);
                  setReviews([]);
                  setPendingDrafts([]);
                  setPhotos([]);
                }}
                className="w-full min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {connectedLocations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.locationName || 'Google Business location'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Upcoming — skinny strip, tap a chip for details (matches Instagram/Facebook) */}
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-gray-900">
                <CalendarDays className="h-4 w-4 text-gray-500" />
                <h3 className="text-[13px] font-semibold" style={{ letterSpacing: '-0.01em' }}>
                  Upcoming
                  {pendingDrafts.length > 0 ? (
                    <span className="text-gray-500 font-medium">
                      {' '}
                      · {pendingDrafts.length} staged
                    </span>
                  ) : null}
                </h3>
              </div>
              {autoPostsEnabled && (
                <button
                  type="button"
                  onClick={() => void ensureDraftQueue()}
                  disabled={fillingQueue || loadingDrafts}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 text-[11px] font-semibold hover:bg-blue-100 disabled:opacity-50"
                >
                  {fillingQueue ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  {fillingQueue ? 'Staging…' : 'Stage to 5'}
                </button>
              )}
            </div>

            {(loadingDrafts || fillingQueue) && pendingDrafts.length === 0 ? (
              <div className="flex gap-2">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-[60px] flex-1 rounded-xl border border-gray-200 bg-gray-100 animate-pulse"
                  />
                ))}
              </div>
            ) : pendingDrafts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 px-4 py-3 text-[13px] text-gray-500 flex items-center justify-between gap-2">
                <span>{autoPostsEnabled ? 'No staged posts yet' : 'No upcoming posts'}</span>
                {autoPostsEnabled && (
                  <button
                    type="button"
                    onClick={() => void ensureDraftQueue()}
                    disabled={fillingQueue}
                    className="shrink-0 text-[13px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    Stage 5 now
                  </button>
                )}
              </div>
            ) : (
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {pendingDrafts.map((d) => {
                  const rel = d.media_library;
                  const url = Array.isArray(rel) ? rel[0]?.url : rel?.url;
                  const isManual = (d.source || 'ai') === 'manual';
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setOpenDraft(d)}
                      className={`flex shrink-0 items-center gap-2.5 rounded-xl border bg-white px-2.5 py-2 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04)] active:bg-gray-50 touch-manipulation ${
                        isManual ? 'border-gray-200' : 'border-amber-200'
                      }`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {url ? (
                        <img
                          src={url}
                          alt=""
                          className="h-10 w-10 rounded-lg object-cover border border-gray-100 bg-gray-50"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-400">
                          {isManual ? (
                            <Clock className="h-4 w-4" />
                          ) : (
                            <Sparkles className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5 text-gray-400" />
                        <span className="text-[13px] font-semibold text-gray-800 whitespace-nowrap">
                          {d.scheduled_for
                            ? formatStripWhen(d.scheduled_for, displayTz)
                            : 'Date pending'}
                        </span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300" />
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Main view — reviews left, posts right */}
          <div className="grid grid-cols-2 items-start gap-3">{/* two-column body */}

            <section className="min-w-0 space-y-2">
              <div className="flex items-center gap-1.5 text-gray-900">
                <MessageSquare className="h-4 w-4 text-gray-500" />
                <h3 className="text-[13px] font-semibold" style={{ letterSpacing: '-0.01em' }}>
                  Reviews
                  {totalReviews != null ? (
                    <span className="text-gray-500 font-medium"> · {totalReviews}</span>
                  ) : null}
                </h3>
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                {([
                  ['all', 'All'],
                  ['replied', 'Replied'],
                  ['unreplied', 'Unreplied'],
                ] as const).map(([value, label]) => {
                  const count =
                    value === 'all'
                      ? reviews.length
                      : reviews.filter((r) =>
                          value === 'replied'
                            ? !!reviewReplyComment(r)
                            : !reviewReplyComment(r),
                        ).length;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setReviewFilter(value)}
                      className={`px-2 py-1 text-[11px] font-semibold rounded-lg border transition-colors ${
                        reviewFilter === value
                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                      }`}
                    >
                      {label}
                      <span
                        className={`ml-1 ${
                          reviewFilter === value ? 'text-blue-500' : 'text-gray-400'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {loadingReviews && reviews.length === 0 ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-[88px] rounded-xl border border-gray-200 bg-gray-100 animate-pulse"
                    />
                  ))}
                </div>
              ) : reviews.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-[12px] text-gray-500">
                  No reviews found yet
                </div>
              ) : (() => {
                const filtered = reviews.filter((r) => {
                  if (reviewFilter === 'all') return true;
                  const replied = !!reviewReplyComment(r);
                  return reviewFilter === 'replied' ? replied : !replied;
                });
                const shown = filtered.slice(0, reviewsVisible);
                const remaining = filtered.length - shown.length;
                return (
                <>
                <ul className="space-y-2">
                  {shown
                    .map((r) => {
                      const stars = starsFor(r);
                      const name = r.reviewer?.displayName || 'Anonymous';
                      const reviewKey = shortReviewId(r.id) || shortReviewId(r.name);
                      const isAiReply = !!(reviewKey && aiReplyIds.has(reviewKey));
                      const replied = !!reviewReplyComment(r);
                      return (
                        <li key={r.id}>
                          <button
                            type="button"
                            onClick={() => setOpenReview(r)}
                            className="w-full text-left bg-white border border-gray-200 rounded-xl p-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] active:bg-gray-50 touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <div className="flex items-baseline justify-between gap-2 min-w-0">
                              <p className="text-[12px] font-semibold text-gray-900 truncate">
                                {name}
                              </p>
                              {r.createTime ? (
                                <span className="text-[10px] text-gray-400 tabular-nums shrink-0">
                                  {formatReviewWhen(r.createTime, displayTz)}
                                </span>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-0.5 mt-0.5">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-3 w-3 ${
                                    i < stars ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            {r.comment && (
                              <p className="mt-1 text-[12px] text-gray-600 line-clamp-2">
                                {r.comment}
                              </p>
                            )}
                            <p
                              className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold ${
                                replied ? 'text-green-700' : 'text-amber-700'
                              }`}
                            >
                              {replied ? (
                                <>
                                  {isAiReply && <Sparkles className="h-3 w-3" />}
                                  {isAiReply ? 'AI replied' : 'Replied'}
                                </>
                              ) : (
                                'Needs reply'
                              )}
                            </p>
                          </button>
                        </li>
                      );
                    })}
                </ul>
                {remaining > 0 ? (
                  <button
                    type="button"
                    onClick={() => setReviewsVisible((n) => n + REVIEWS_PAGE_SIZE)}
                    className="mt-2 w-full rounded-xl border border-gray-200 bg-white py-2 text-[12px] font-semibold text-[#1a73e8] hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    View more · {Math.min(remaining, REVIEWS_PAGE_SIZE)} of {remaining} remaining
                  </button>
                ) : null}
                </>
                );
              })()}
            </section>

            {isNativeGoogle ? (() => {
              const cover = photos.find(isCoverPhoto) || null;
              const gallery = photos.filter((p) => !isCoverPhoto(p) && !isProfilePhoto(p));
              const shownGallery = gallery.slice(0, 12);
              const postTile = (p: GbpPublishedPost) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setOpenPost(p)}
                  className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 text-left touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {p.mediaUrl ? (
                    <>
                      <img
                        src={gbpThumb(p.mediaUrl, 400)}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-x-0 bottom-0 px-1.5 pb-1 pt-5 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                        <p className="text-[9px] font-semibold text-white leading-snug line-clamp-2 drop-shadow-sm">
                          {p.content}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center p-1.5 text-center bg-white border border-gray-100 rounded-lg">
                      <p className="text-[9px] text-gray-600 line-clamp-5 whitespace-pre-wrap">
                        {p.content || 'No caption'}
                      </p>
                    </div>
                  )}
                </button>
              );
              return (
                <section className="min-w-0 space-y-3">
                  {/* Cover photo — the listing's hero */}
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-[16/9]">
                    {cover ? (
                      <button
                        type="button"
                        onClick={() => openPhotos({ detailName: cover.name || null })}
                        className="block w-full h-full"
                        aria-label="View cover photo"
                      >
                        <GbpPhotoImg photo={cover} alt="Cover photo" className="w-full h-full object-cover" size={1200} crop={false} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openPhotos({ roleMenu: 'COVER' })}
                        className="w-full h-full flex flex-col items-center justify-center text-center px-3"
                      >
                        <ImagePlus className="h-6 w-6 text-[#1a73e8] mb-1" />
                        <p className="text-[12px] font-semibold text-gray-800">Add a cover photo</p>
                        <p className="text-[11px] text-gray-500">It's the first thing customers see on Google</p>
                      </button>
                    )}
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-gray-900/80 text-white">
                      <Star className="h-2.5 w-2.5" />
                      Cover
                    </span>
                    <button
                      type="button"
                      onClick={() => openPhotos({ roleMenu: 'COVER' })}
                      className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/95 border border-gray-200 text-gray-800 text-[11px] font-semibold shadow-sm hover:bg-white"
                    >
                      <Replace className="h-3 w-3" />
                      {cover ? 'Change' : 'Add'}
                    </button>
                  </div>

                  {/* Photo grid */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-gray-900">
                      <ImageIcon className="h-4 w-4 text-gray-500" />
                      <h3 className="text-[13px] font-semibold" style={{ letterSpacing: '-0.01em' }}>
                        Photos
                        {gallery.length > 0 ? (
                          <span className="text-gray-500 font-medium"> · {gallery.length}</span>
                        ) : null}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => openPhotos()}
                      className="text-[12px] font-semibold text-[#1a73e8] hover:underline"
                    >
                      {gallery.length > 0 ? 'Manage' : 'Add photos'}
                    </button>
                  </div>
                  {loadingPhotos && photos.length === 0 ? (
                    <div className="grid grid-cols-3 gap-1.5">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="aspect-square rounded-lg bg-gray-100 animate-pulse" />
                      ))}
                    </div>
                  ) : gallery.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => openPhotos()}
                      className="w-full rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-[12px] text-gray-500 hover:bg-gray-50"
                    >
                      No photos on your Google profile yet — tap to add some
                    </button>
                  ) : (
                    <div className="grid grid-cols-3 gap-1.5">
                      {shownGallery.map((photo, idx) => (
                        <button
                          key={photo.name || idx}
                          type="button"
                          onClick={() => openPhotos({ detailName: photo.name || null })}
                          className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <GbpPhotoImg
                            photo={photo}
                            alt={photo.description || photoCategory(photo)}
                            className="absolute inset-0 w-full h-full object-cover"
                            size={400}
                          />
                        </button>
                      ))}
                      {gallery.length > shownGallery.length ? (
                        <button
                          type="button"
                          onClick={() => openPhotos()}
                          className="aspect-square rounded-lg bg-gray-100 text-gray-700 text-[12px] font-semibold flex items-center justify-center hover:bg-gray-200"
                        >
                          +{gallery.length - shownGallery.length} more
                        </button>
                      ) : null}
                    </div>
                  )}

                  {/* Every post on Google */}
                  <div className="flex items-center gap-1.5 text-gray-900 pt-1">
                    <Grid3x3 className="h-4 w-4 text-gray-500" />
                    <h3 className="text-[13px] font-semibold" style={{ letterSpacing: '-0.01em' }}>
                      Posts
                      {publishedPosts.length > 0 ? (
                        <span className="text-gray-500 font-medium"> · {publishedPosts.length}</span>
                      ) : null}
                    </h3>
                  </div>
                  {loadingPublished && publishedPosts.length === 0 ? (
                    <div className="grid grid-cols-3 gap-1.5">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="aspect-square rounded-lg bg-gray-100 animate-pulse" />
                      ))}
                    </div>
                  ) : publishedPosts.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-[12px] text-gray-500">
                      No posts on your Google profile yet
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-1.5">{publishedPosts.map(postTile)}</div>
                  )}
                </section>
              );
            })() : (
            <section className="min-w-0 space-y-2">
              <div className="flex items-center gap-1.5 text-gray-900">
                <Grid3x3 className="h-4 w-4 text-gray-500" />
                <h3 className="text-[13px] font-semibold" style={{ letterSpacing: '-0.01em' }}>
                  Posts
                  {publishedPosts.length > 0 ? (
                    <span className="text-gray-500 font-medium"> · {publishedPosts.length}</span>
                  ) : null}
                </h3>
              </div>

              {loadingPublished && publishedPosts.length === 0 ? (
                <div className="grid grid-cols-2 gap-1.5">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="aspect-square rounded-lg bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : publishedPosts.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-[12px] text-gray-500">
                  No posts published from Nexus yet
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-1.5">
                  {publishedPosts.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setOpenPost(p)}
                      className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 text-left touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {p.mediaUrl ? (
                        <>
                          <img
                            src={gbpThumb(p.mediaUrl, 400)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                          <div className="absolute inset-x-0 bottom-0 px-2 pb-1.5 pt-6 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                            <p className="text-[10px] font-semibold text-white leading-snug line-clamp-2 drop-shadow-sm">
                              {p.content}
                            </p>
                          </div>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center p-2 text-center bg-white border border-gray-100 rounded-lg">
                          <p className="text-[10px] text-gray-600 line-clamp-5 whitespace-pre-wrap">
                            {p.content || 'No caption'}
                          </p>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>
            )}
          </div>

          {/* Native Google — full-featured sheets */}
          {isNativeGoogle && businessId && selectedConnectionId ? (
            <>
              <GoogleBusinessInsightsSheet
                businessId={businessId}
                connectionId={selectedConnectionId}
                locationName={locationName}
                open={showInsights}
                onClose={() => setShowInsights(false)}
              />
              <GoogleBusinessProfilePanel
                businessId={businessId}
                connectionId={selectedConnectionId}
                open={showProfile}
                onClose={() => setShowProfile(false)}
                onLocationRenamed={(t) => {
                  setLocationName(t);
                  void refreshConnection();
                }}
              />
              <GoogleBusinessPhotosPanel
                businessId={businessId}
                connectionId={selectedConnectionId}
                open={showPhotos}
                onClose={() => {
                  setShowPhotos(false);
                  setPhotosEntry({ roleMenu: null, detailName: null });
                  void loadPublishedPosts();
                }}
                onChanged={(items) => setPhotos(items as unknown as GbpPhoto[])}
                initialRoleMenu={photosEntry.roleMenu}
                initialDetailName={photosEntry.detailName}
              />
            </>
          ) : null}

          {/* Photos manager (aggregator connections) — Nexus-published photos only */}
          {showPhotos && !isNativeGoogle && (() => {
            const cover = photos.find(isCoverPhoto) || null;
            const otherPhotos = photos.filter((p) => !isCoverPhoto(p));
            return (
              <div className="fixed inset-0 z-[100080] flex items-end sm:items-center justify-center p-0 sm:p-6">
                <div
                  className="absolute inset-0 bg-black/40"
                  onClick={() => setShowPhotos(false)}
                />
                <div
                  className="relative bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col shadow-xl overflow-hidden"
                  style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
                    <h3 className="text-sm font-semibold text-gray-900">Google Business photos</h3>
                    <button
                      type="button"
                      onClick={() => setShowPhotos(false)}
                      aria-label="Close"
                      className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="p-4 overflow-y-auto space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoUploadMode('additional');
                      photoFileInputRef.current?.click();
                    }}
                    disabled={uploadingPhoto}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {uploadingPhoto && photoUploadMode === 'additional' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImagePlus className="h-4 w-4" />
                    )}
                    Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoUploadMode('additional');
                      setShowMediaPicker(true);
                    }}
                    disabled={uploadingPhoto || !businessId}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ImageIcon className="h-4 w-4" />
                    From Media library
                  </button>
                  <input
                    ref={photoFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => void handlePhotoFileUpload(e)}
                  />
                  <button
                    type="button"
                    onClick={() => void loadPhotos()}
                    disabled={loadingPhotos}
                    className="ml-auto inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${loadingPhotos ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                {loadingPhotos && photos.length === 0 ? (
                  <div className="flex justify-center py-12 text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : photos.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-xl px-4 py-10 text-center">
                    <ImageIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No Google Business photos yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {/* Cover — first cell, same size, highlighted */}
                    <div className="relative bg-white rounded-xl overflow-hidden border-2 border-blue-500 ring-2 ring-blue-100">
                      <div className="aspect-square bg-blue-50/40">
                        {cover && photoSrc(cover) ? (
                          <GbpPhotoImg
                            photo={cover}
                            alt={cover.description || 'Cover photo'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-center px-2">
                            <ImageIcon className="h-7 w-7 text-blue-300 mb-1" />
                            <p className="text-[11px] text-gray-500">No cover</p>
                          </div>
                        )}
                      </div>
                      <div className="absolute top-1.5 left-1.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-blue-600 text-white shadow-sm">
                          Cover
                        </span>
                      </div>
                      <div className="absolute top-1.5 right-1.5">
                        <button
                          type="button"
                          onClick={() => setShowCoverSwapMenu((v) => !v)}
                          disabled={uploadingPhoto}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/95 border border-blue-200 text-blue-700 text-[10px] font-semibold shadow-sm hover:bg-white disabled:opacity-50"
                        >
                          {uploadingPhoto && photoUploadMode === 'cover' ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Replace className="h-3 w-3" />
                          )}
                          Swap
                        </button>
                        {showCoverSwapMenu && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setShowCoverSwapMenu(false)}
                            />
                            <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-1 overflow-hidden">
                              <button
                                type="button"
                                onClick={startCoverSwapUpload}
                                className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                              >
                                Upload new
                              </button>
                              <button
                                type="button"
                                onClick={startCoverSwapFromMedia}
                                className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                              >
                                From Media library
                              </button>
                              <button
                                type="button"
                                onClick={startCoverSwapFromGbp}
                                disabled={otherPhotos.length === 0}
                                className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                              >
                                From Google photos
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {otherPhotos.map((photo, idx) => {
                      const src = photoSrc(photo);
                      const category = photoCategory(photo);
                      return (
                        <div
                          key={photo.name || src || idx}
                          className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                        >
                          <div className="aspect-square bg-gray-50">
                            <GbpPhotoImg
                              photo={photo}
                              alt={photo.description || category}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pick existing GBP photo as cover */}
                {showCoverFromGbp && (
                  <div className="fixed inset-0 z-[100090] flex items-end sm:items-center justify-center p-0 sm:p-6">
                    <div
                      className="absolute inset-0 bg-black/40"
                      onClick={() => setShowCoverFromGbp(false)}
                    />
                    <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[80vh] flex flex-col shadow-xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900">
                          Choose cover photo
                        </h3>
                        <button
                          type="button"
                          onClick={() => setShowCoverFromGbp(false)}
                          className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="p-4 overflow-y-auto">
                        {otherPhotos.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-8">
                            No other Google photos available
                          </p>
                        ) : (
                          <div className="grid grid-cols-3 gap-2">
                            {otherPhotos.map((photo, idx) => {
                              const src = photoSrc(photo);
                              if (!src) return null;
                              return (
                                <button
                                  key={photo.name || src || idx}
                                  type="button"
                                  disabled={uploadingPhoto}
                                  onClick={() =>
                                    void uploadPhotoToGoogle(
                                      photo.googleUrl || photo.sourceUrl || src,
                                      'COVER',
                                    )
                                  }
                                  className="aspect-square rounded-xl overflow-hidden border border-gray-200 hover:border-blue-500 hover:ring-2 hover:ring-blue-200 transition-all disabled:opacity-50"
                                >
                                  <GbpPhotoImg
                                    photo={photo}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Upcoming draft detail sheet */}
          {openDraft ? (
            <div
              className="fixed inset-0 z-[100080] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px] p-0 sm:p-4"
              onClick={() => setOpenDraft(null)}
            >
              <div
                className="w-full sm:max-w-md bg-white rounded-t-[20px] sm:rounded-[20px] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
              >
                {(() => {
                  const d = openDraft;
                  const rel = d.media_library;
                  const url = Array.isArray(rel) ? rel[0]?.url : rel?.url;
                  const isManual = (d.source || 'ai') === 'manual';
                  const scheduledLabel = d.scheduled_for
                    ? new Date(d.scheduled_for).toLocaleString(undefined, {
                        timeZone: displayTz,
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZoneName: 'short',
                      })
                    : null;
                  return (
                    <>
                      <div className="flex items-center justify-between px-5 pt-4 pb-2">
                        <div className="flex items-center gap-2">
                          {isManual ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-800 border border-gray-200">
                              Your post
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200">
                              <Sparkles className="h-3 w-3" />
                              AI draft
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-500">
                            <Clock className="h-3.5 w-3.5" />
                            {scheduledLabel
                              ? `Publishes ${scheduledLabel}`
                              : 'Date pending…'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOpenDraft(null)}
                          aria-label="Close"
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="overflow-y-auto px-5 pb-4">
                        {url ? (
                          <img
                            src={url}
                            alt=""
                            className="w-full rounded-xl object-cover border border-gray-100 bg-gray-50 mb-3 max-h-72"
                          />
                        ) : d.has_image ? (
                          <div className="w-full h-32 rounded-xl border border-dashed border-blue-200 bg-blue-50 mb-3 flex items-center justify-center text-[12px] font-semibold text-blue-600">
                            Image attached
                          </div>
                        ) : null}
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{d.content}</p>
                      </div>
                      <div className="shrink-0 border-t border-gray-100 px-5 py-3 flex gap-2">
                        <button
                          type="button"
                          onClick={() => void handleDraftAction(d.id, 'approve')}
                          disabled={draftActionId === d.id}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[10px] bg-[#1a73e8] text-white text-[13px] font-semibold active:bg-[#1765cc] disabled:opacity-50 touch-manipulation"
                          style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
                        >
                          {draftActionId === d.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                          Publish now
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDraftAction(d.id, 'reject')}
                          disabled={draftActionId === d.id}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-[10px] border border-gray-300 text-gray-700 text-[13px] font-semibold active:bg-gray-50 disabled:opacity-50 touch-manipulation"
                          style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
                        >
                          <X className="h-4 w-4" />
                          Discard
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          ) : null}

          {/* Review detail + reply sheet */}
          {openReview ? (
            <div
              className="fixed inset-0 z-[100080] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px] p-0 sm:p-4"
              onClick={() => setOpenReview(null)}
            >
              <div
                className="w-full sm:max-w-md bg-white rounded-t-[20px] sm:rounded-[20px] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
              >
                {(() => {
                  const r = openReview;
                  const stars = starsFor(r);
                  const name = r.reviewer?.displayName || 'Anonymous';
                  const reviewKey = shortReviewId(r.id) || shortReviewId(r.name);
                  const isAiReply = !!(reviewKey && aiReplyIds.has(reviewKey));
                  return (
                    <>
                      <div className="flex items-start justify-between px-5 pt-4 pb-2 gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="flex items-center gap-0.5">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-3.5 w-3.5 ${
                                    i < stars ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </span>
                            {r.createTime && (
                              <span className="text-xs text-gray-400 tabular-nums">
                                {formatReviewWhen(r.createTime, displayTz)}
                                {r.updateTime && r.updateTime !== r.createTime ? ' · edited' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOpenReview(null)}
                          aria-label="Close"
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 touch-manipulation shrink-0"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="overflow-y-auto px-5 pb-5 space-y-3">
                        {r.comment && (
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.comment}</p>
                        )}
                        {reviewReplyComment(r) ? (
                          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="text-xs font-medium text-gray-500">Your reply</p>
                              {isAiReply && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200">
                                  <Sparkles className="h-3 w-3" />
                                  AI reply
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                              {reviewReplyComment(r)}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {isPreConnectionReview(r) && (
                              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                                This review is from before you connected — if you already replied
                                on Google, that reply may still be syncing and posting here would
                                replace it. Check Google first if unsure.
                              </p>
                            )}
                            <textarea
                              value={replyDrafts[r.id] || ''}
                              onChange={(e) =>
                                setReplyDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))
                              }
                              rows={3}
                              placeholder="Write a public reply…"
                              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() => void handleReply(r)}
                              disabled={replyingId === r.id}
                              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-[10px] bg-[#1a73e8] text-white text-[13px] font-semibold active:bg-[#1765cc] disabled:opacity-50 touch-manipulation"
                              style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
                            >
                              {replyingId === r.id && (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              )}
                              Reply
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          ) : null}

          {/* Published post detail sheet */}
          {openPost ? (
            <div
              className="fixed inset-0 z-[100080] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px] p-0 sm:p-4"
              onClick={() => setOpenPost(null)}
            >
              <div
                className="w-full sm:max-w-md bg-white rounded-t-[20px] sm:rounded-[20px] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
              >
                <div className="flex items-center justify-between px-5 pt-4 pb-2">
                  <span className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-500">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {openPost.createdAt
                      ? `Published ${new Date(openPost.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}`
                      : 'Published'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpenPost(null)}
                    aria-label="Close"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="overflow-y-auto px-5 pb-5">
                  {openPost.mediaUrl && (
                    <img
                      src={openPost.mediaUrl}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="w-full rounded-xl object-cover border border-gray-100 bg-gray-50 mb-3 max-h-72"
                    />
                  )}
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{openPost.content}</p>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}

      {businessId && (
        <MediaLibraryPicker
          isOpen={showMediaPicker}
          businessId={businessId}
          multiple={false}
          onClose={() => {
            setShowMediaPicker(false);
            setPhotoUploadMode('additional');
          }}
          onDone={(selected) => void handleLibraryPick(selected)}
        />
      )}

      <CreateGbpPostModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        connectionId={selectedConnectionId}
        onSuccess={() => {
          void loadPendingDrafts();
        }}
      />

      {disconnectModal}
    </div>
  );
}
